// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trophy, Calendar, MapPin, Users, Search, ArrowLeft } from 'lucide-react';

interface TournamentEvent {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  venue_name: string;
  venue_city: string;
  venue_state: string;
}

interface Tournament {
  id: string;
  name: string;
  tournament_type: string;
  status: string;
  division_name: string;
  created_at: string;
  events: TournamentEvent | TournamentEvent[] | null;
  _count?: {
    teams: number;
  };
}

const TournamentDiscoveryPage: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      const { data, error } = await supabase
        .from('tournaments')
        .select(`
          id,
          name,
          tournament_type,
          status,
          division_name,
          created_at,
          events (
            id,
            name,
            start_date,
            end_date,
            venue_name,
            venue_city,
            venue_state
          )
        `)
        .in('status', ['active', 'setup', 'completed'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get team counts for each tournament
      const tournamentsWithCounts = await Promise.all(
        (data || []).map(async (tournament) => {
          const { count } = await supabase
            .from('tournament_brackets')
            .select('*', { count: 'exact', head: true })
            .eq('tournament_id', tournament.id);

          return {
            ...tournament,
            _count: { teams: count || 0 }
          };
        })
      );

      setTournaments(tournamentsWithCounts);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get event data (handles both single object and array)
  const getEventData = (events: TournamentEvent | TournamentEvent[] | null): TournamentEvent | null => {
    if (!events) return null;
    if (Array.isArray(events)) return events[0] || null;
    return events;
  };

  const filteredTournaments = tournaments.filter(tournament => {
    const event = getEventData(tournament.events);
    const matchesSearch = searchQuery === '' || 
      tournament.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tournament.division_name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || tournament.status === statusFilter;
    const matchesType = typeFilter === 'all' || tournament.tournament_type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusVariant = (status: string): "default" | "secondary" | "success" | "outline" => {
    switch (status) {
      case 'active': return 'success';
      case 'setup': return 'secondary';
      case 'completed': return 'outline';
      default: return 'outline';
    }
  };

  const getTournamentTypeLabel = (type: string) => {
    switch (type) {
      case 'single_elimination': return 'Single Elimination';
      case 'double_elimination': return 'Double Elimination';
      case 'round_robin': return 'Round Robin';
      case 'pool_play_playoffs': return 'Pool Play + Playoffs';
      default: return type;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Simple Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
          <h1 className="text-xl font-bold text-foreground">Tournaments</h1>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-8">
        {/* Description */}
        <p className="text-muted-foreground mb-6">Browse upcoming and active tournaments</p>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search tournaments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="setup">Upcoming</SelectItem>
              <SelectItem value="active">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="single_elimination">Single Elimination</SelectItem>
              <SelectItem value="double_elimination">Double Elimination</SelectItem>
              <SelectItem value="round_robin">Round Robin</SelectItem>
              <SelectItem value="pool_play_playoffs">Pool Play + Playoffs</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center min-h-64">
            <div className="text-lg text-muted-foreground">Loading tournaments...</div>
          </div>
        ) : filteredTournaments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Trophy className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Tournaments Found</h3>
              <p className="text-muted-foreground">
                {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Check back soon for upcoming tournaments'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredTournaments.map((tournament) => {
              const event = getEventData(tournament.events);
              return (
                <Card key={tournament.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start gap-2">
                      <CardTitle className="text-lg line-clamp-2">
                        {tournament.name || event?.name || 'Untitled Tournament'}
                      </CardTitle>
                      <Badge variant={getStatusVariant(tournament.status)}>
                        {tournament.status === 'setup' ? 'Upcoming' : tournament.status}
                      </Badge>
                    </div>
                    {tournament.division_name && (
                      <Badge variant="outline" className="w-fit">{tournament.division_name}</Badge>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {event && (
                        <>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="w-4 h-4 flex-shrink-0" />
                            <span>
                              {formatDate(event.start_date)}
                              {event.end_date && event.end_date !== event.start_date && (
                                <> - {formatDate(event.end_date)}</>
                              )}
                            </span>
                          </div>
                          {event.venue_name && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              <span className="line-clamp-1">
                                {event.venue_name}
                                {event.venue_city && `, ${event.venue_city}`}
                                {event.venue_state && `, ${event.venue_state}`}
                              </span>
                            </div>
                          )}
                        </>
                      )}

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {getTournamentTypeLabel(tournament.tournament_type)}
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Users className="w-4 h-4" />
                          {tournament._count?.teams || 0} teams
                        </span>
                      </div>

                      <Link to={`/tournaments/${tournament.id}/bracket`}>
                        <Button className="w-full mt-2">
                          <Trophy className="w-4 h-4 mr-2" />
                          View Bracket
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default TournamentDiscoveryPage;